import {
    useMutation,
    useQuery,
    useQueryClient
} from '@tanstack/react-query';
import {
    add,
    deleteRecord,
    fetch,
    update
} from '../services/CustomersService';
import {Customer} from '../types';
import {notifySuccess, notifyError} from '../../../components/ServiceNotifications';

export const useCustomer = (customerId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const customerQuery = useQuery({
                                       queryKey: ['customer', customerId],
                                       queryFn: () => fetch(customerId!),
                                       enabled: !!customerId
                                   });

    // CREATE MUTATION
    const createMutation = useMutation({
                                           mutationFn: (values: Customer) => {
                                               const {
                                                   created: _created,
                                                   createdByUsername: _createdByUsername,
                                                   createdByUser: _createdByUser,
                                                   updated: _updated,
                                                   updatedByUsername: _updatedByUsername,
                                                   ...rest
                                               } = values;
                                               return add(rest);
                                           },
                                           onSuccess: () => {
                                               notifySuccess('Customer created');
                                               queryClient.invalidateQueries({queryKey: ['customers']});
                                               queryClient.invalidateQueries({queryKey: ['all-customers']});
                                           },
                                           onError: notifyError
                                       });

    // UPDATE MUTATION
    const updateMutation = useMutation({
                                           mutationFn: (values: Customer) => {
                                               if (!customerId) throw new Error('customerId is required parameter');
                                               return update(customerId, values);
                                           },
                                           onSuccess: () => {
                                               notifySuccess('Customer updated');
                                               queryClient.invalidateQueries({queryKey: ['customers']});
                                               queryClient.invalidateQueries({queryKey: ['all-customers']});
                                               queryClient.invalidateQueries({queryKey: ['customer', customerId]});
                                           },
                                           onError: notifyError
                                       });

    // DELETE MUTATION
    const deleteMutation = useMutation({
                                           mutationFn: () => {
                                               if (!customerId) throw new Error('customerId is required parameter');
                                               return deleteRecord(customerId);
                                           },
                                           onSuccess: () => {
                                               notifySuccess('Customer deleted');
                                               queryClient.invalidateQueries({queryKey: ['customers']});
                                               queryClient.invalidateQueries({queryKey: ['all-customers']});
                                           },
                                           onError: notifyError
                                       });

    return {
        record: customerQuery.data,
        loading: customerQuery.isLoading,
        fetchRecord: customerQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};